//import { config } from '@/config';
import firebase from 'firebase/app';
import 'firebase/messaging';
import serviceAPI from '@/services/serviceAPI';
import {
  MailNotificationInterface,
  MiniUserInterface,
  NotifySubscriberPayloadInterface,
  RecipientInterface,
} from '@/types/interfaces';
import * as enums from '@/types/enums';
import { Dictionary } from 'vue-router/types/router';

const getDefaultState = () => {
  return {
    toDelete: false,
    sectionId: '',
  };
};

/**
 * Computes the subscribers for an event
 * @param payload Data needed for getting subscribers
 * @param participants Participants of the specific document
 * @param documentId The specific document that the event refers to
 * @param currentUser The logged in user object
 * @returns An array of subscribers
 */
const getSubscribersForNotification = (
  payload: NotifySubscriberPayloadInterface,
  participants: Dictionary<MiniUserInterface>,
  documentId: string,
  currentUser: MiniUserInterface
) => {
  const result: string[] = []
  for (const key in participants) {
    const participant = participants[key];
    if (shouldAddParticipantForTrigger(participant, currentUser, payload.trigger, documentId, payload.sectionId)) {
      result.push(participant.id);
    }
  }
  return result;
}

/**
 * Checks whether a participant of a document should be notified of an event
 * @param participant The participant
 * @param currentUser The logged in user object
 * @param trigger The type of the event
 * @param documentId The specific document that the event refers to
 * @param sectionId The specific section that the event refers to
 * @returns {boolean} true if the participant should be notified, false otherwise
 */
const shouldAddParticipantForTrigger = (
  participant: MiniUserInterface,
  currentUser: MiniUserInterface,
  trigger: enums.NOTIFICATION_TRIGGER,
  documentId: string,
  sectionId: string
) => {
  if (participant.id === currentUser.id) {
    return false;
  }
  const { suggestionsNotifications, documents } = participant.notifications || {};
  switch (trigger) {
    case enums.NOTIFICATION_TRIGGER.argumentCreation:
      return !!(suggestionsNotifications?.[sectionId] || documents?.[documentId]?.newArgumentOrCommentNotification);
    case enums.NOTIFICATION_TRIGGER.sectionApproval:
      return !!documents?.[documentId]?.newDocumentVersionNotification;
    case enums.NOTIFICATION_TRIGGER.sectionEdit:
      return !!(suggestionsNotifications?.[sectionId] || documents?.[documentId]?.newEditSuggestionNotification);
    case enums.NOTIFICATION_TRIGGER.sectionCreation:
      return !!documents?.[documentId]?.newSectionSuggestionNotification;
  }
}

const Domain = process.env.VUE_APP_FIREBASE_DOMAIN || 'https://localhost:5000';
let messaging
if (firebase.messaging.isSupported()) {
  messaging = firebase.messaging();
}

export const notificationsModule = {
  namespaced: true,
  state: {
    toDelete: false,
    sectionId: '',
    emailConfig: {
      recipientsArray: [],
      title: '',
      body: { text: '', url: '' },
    },
  },
  getters: {
    toDelete: (state) => state.toDelete,
    sectionId: (state) => state.sectionId,
  },
  mutations: {
    resetState(state) {
      Object.assign(state, getDefaultState());
    },
  },
  actions: {
    /**
     * The wrapper function that notifies the subscribers of an event
     * @param payload Contains the event data (trigger, document, sections)
     * @returns 
     */
    notifySubscribers: async ({ rootGetters, dispatch }, payload: NotifySubscriberPayloadInterface) => {
      const { body, title, trigger, sectionId } = payload;
      const documentId = rootGetters['documentsModule/documentId'];
      const recipientsList: RecipientInterface[] = await dispatch(
        'getBatchRecipients',
        getSubscribersForNotification(
          payload,
          rootGetters['usersModule/participants'],
          documentId,
          rootGetters['usersModule/user']
        )
      );

      if (!title || !body || !recipientsList.length) {
        return
      }

      const emailConfig: MailNotificationInterface = {
        authDomain: Domain,
        body,
        documentId,
        recipientsList,
        sectionId,
        title,
        trigger
      }

      await dispatch('sendNotifications', emailConfig);
    },

    /**
     * sending notifications to users
     * @param {object} emailConfig email configuration to send
     */
    sendNotifications: async ({ commit }, emailConfig: MailNotificationInterface) => {
      if (emailConfig.documentId === 'springfield') {
        await serviceAPI.sendNotifications(emailConfig);
      }
      await serviceAPI.sendPushNotifications(emailConfig);
      commit('resetState');
    },

    getBatchRecipients: async ({ dispatch }, recipients: [string] | string) => {
      return await dispatch('usersModule/getBatchUsersNotificationProp', { users: recipients }, { root: true });
    },

    checkFcmRegistrationToken: async ({ dispatch, commit, rootGetters }) => {
      console.log('checkFcmRegistrationToken is running')
      try {
        const token = await messaging?.getToken({ vapidKey: process.env.VUE_APP_CLOUD_MESSAGING_CLIENT_KEY });
        console.log('checkFcmRegistrationToken token: ', token)
        if (token) {
          const currentMessageToken = rootGetters['usersModule/userMsgRegistrationToken'];
          console.log('token will be updated. token:', currentMessageToken, currentMessageToken != token);

          if (currentMessageToken != token) {
            await dispatch('saveToken', token)
          }
        } else {
          console.log('No Instance ID token available. Request permission to generate one.');
          await dispatch('notificationsPermissionRequest');
        }
      } catch (err) {
        console.log('An error occurred while retrieving token. ', err);
      }
    },

    notificationsPermissionRequest: async ({ dispatch, commit, rootGetters }) => {
      console.log('request permission is running')
      messaging?.requestPermission()
        .then(() => {
          dispatch('checkFcmRegistrationToken');
        })
        .catch((err) => {
          console.log('Unable to get permission to notify.', err);
        });
    },

    saveToken: async ({ dispatch, commit, rootGetters }, token) => {
      console.log('saveToken token', token);
      const currentUser = rootGetters['usersModule/user'];
      console.log('saveToken currentUser: ', currentUser)
      const notifications = {
        ...currentUser.notifications,
        msgRegistrationToken: token
      }
      await dispatch('usersModule/updateCurrentUser', { updateObject: { notifications } }, { root: true });
    },

    handleOnScreenMsg: async () => {
      messaging?.onMessage(payload => {
        console.log('onMessage payload: ', payload)
        const { title, body } = payload.data;
        alert(`${title} - ${body}`);
      })
    },
  },
  /** for vuex-easy-module */
  firestorePath: '',
  firestoreRefType: '',
  moduleName: '',
  statePropName: '',
  sync: {},
  serverChange: {},
  fetch: {
    docLimit: 0,
  },
};
